/**
 * BrainReviewScreen — the "look at everything you taught Akinex" screen.
 *
 * Shown once every Required question has an answer. Groups the answers
 * by category (Required first, then Recommended, then Optional), and
 * offers the founder two clear next steps:
 *
 *   * Edit One at a Time — jump back into the guided Focus Flow at a
 *     specific question.
 *   * Edit All Answers   — open the structured full-page editor
 *     (``BrainFullEditor``) with every current answer prefilled.
 *
 * Every other tap on an individual answer's "Edit" button behaves the
 * same as it did before Patch 37.4.3 — it opens Focus Flow at that
 * exact question and returns here on save. Nothing about the guided
 * flow contract has changed.
 */
import type { BrainMissionState, BrainQuestion } from "@/entities/brain-missions";

interface BrainReviewScreenProps {
  questions: BrainQuestion[];
  state: BrainMissionState;
  /** Current answers keyed by question id — sourced from the parent. */
  answers: Record<string, unknown>;
  /** Jump back to a specific question in the guided Focus Flow. */
  onEdit: (questionId: string) => void;
  /** Open the guided Focus Flow at the first question (Edit One at a Time). */
  onEditOneAtATime: () => void;
  /** Open the structured full-page editor (Edit All Answers). */
  onEditAllAnswers: () => void;
  /** After a Full Edit save, the parent renders a review-required
   *  banner. Optional so pre-edit renders (Focus Flow's normal review
   *  transition) don't have to supply one. */
  reviewRequiredBanner?: JSX.Element | null;
}

const GROUPS: { key: BrainQuestion["category"]; label: string; blurb: string }[] = [
  { key: "required", label: "The essentials", blurb: "The four answers Akinex needs before it can confidently represent you." },
  { key: "recommended", label: "The details that sharpen everything", blurb: "Not required — but each one makes future campaigns feel more like you." },
  { key: "optional", label: "The colour that makes you unmistakable", blurb: "Nothing here is essential. Anything you add here becomes an asset over time." },
];

export function BrainReviewScreen({
  questions,
  state,
  answers,
  onEdit,
  onEditOneAtATime,
  onEditAllAnswers,
  reviewRequiredBanner,
}: BrainReviewScreenProps) {
  const byCategory = new Map<BrainQuestion["category"], BrainQuestion[]>();
  for (const q of questions) {
    if (!byCategory.has(q.category)) byCategory.set(q.category, []);
    byCategory.get(q.category)!.push(q);
  }
  return (
    <div
      className="mx-auto flex max-w-2xl flex-col gap-6 text-white"
      data-testid="brain-review-screen"
    >
      <div>
        <h2 className="text-2xl font-semibold leading-snug tracking-tight sm:text-3xl">
          Here&apos;s everything you&apos;ve told Akinex.
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-white/70">
          Take a moment to read it back. Anything reads off, edit it —
          you can keep sharpening even after you approve.
        </p>
      </div>

      {reviewRequiredBanner}

      {/* Patch 37.4.3 · two clear secondary actions at the top. */}
      <div
        className="flex flex-col gap-2 rounded-2xl border border-white/10 bg-white/[0.02] p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4"
        data-testid="brain-review-edit-modes"
      >
        <p className="text-xs text-white/60 sm:max-w-xs">
          Want to change something? Pick how you&apos;d like to edit.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={onEditOneAtATime}
            className="rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-white/85 hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
            data-testid="brain-review-edit-one-at-a-time"
          >
            Edit One at a Time
          </button>
          <button
            type="button"
            onClick={onEditAllAnswers}
            className="rounded-full border border-cyan-400/60 bg-cyan-400/10 px-4 py-2 text-xs font-semibold text-cyan-100 hover:bg-cyan-400/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
            data-testid="brain-review-edit-all-answers"
          >
            Edit All Answers
          </button>
        </div>
      </div>

      {GROUPS.map((g) => {
        const list = byCategory.get(g.key) ?? [];
        if (list.length === 0) return null;
        return (
          <section
            key={g.key}
            className="flex flex-col gap-3"
            data-testid={`brain-review-group-${g.key}`}
          >
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-300/80">
                {g.label}
              </div>
              <p className="mt-1 text-xs text-white/50">{g.blurb}</p>
            </div>
            <ul className="flex flex-col gap-2">
              {list.map((q) => {
                const answered = state.answered_ids.includes(q.id);
                const skipped = state.skipped_ids.includes(q.id);
                return (
                  <li
                    key={q.id}
                    className="flex items-start justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-4"
                    data-testid={`brain-review-item-${q.id}`}
                    data-answered={answered}
                    data-skipped={skipped}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-white">
                        {q.label}
                      </div>
                      <div className="mt-1 whitespace-pre-wrap break-words text-xs text-white/70">
                        {answered
                          ? renderAnswer(answers[q.id])
                          : skipped
                            ? "You skipped this for now."
                            : "Not yet answered."}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => onEdit(q.id)}
                      className="shrink-0 rounded-full border border-white/15 px-3 py-1.5 text-xs font-medium text-white/80 hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
                      data-testid={`brain-review-edit-${q.id}`}
                    >
                      {answered ? "Edit" : "Answer"}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function renderAnswer(value: unknown): string {
  if (value == null) return "—";
  if (Array.isArray(value)) return value.join(" · ");
  if (typeof value === "string") return value;
  return String(value);
}
